import { CircularProgress } from '@mui/material'
import { GET_CALENDAR_DATA, getCalendarData, GetCalendarDataPromise } from 'api'
import clsx from 'clsx'
import {
	Button,
	Calendar,
	Checkbox,
	DropdownFull,
	Input,
	Layout,
	Title,
	Tooltip,
	Typography
} from 'components'
import { useAgenda } from 'context'
import { format } from 'date-fns'
import { useSearchDebounce, useToast } from 'hooks'
import { DropdownFullItemProps, Icons, TypographyTypes } from 'interfaces'
import { allContentTypesData, allStatusesCalendar } from 'lib'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { CalendarEvent } from 'utils'
import { eventStyles } from 'utils/eventStyles/EventStyles'

export const Agenda: React.FC = (): JSX.Element => {
	const cookies = new Cookies()
	const { showToast } = useToast()

	const organizationId = cookies.get('organization_id')
	const organizationLogo = cookies.get('organization_logo')
	const organizationName = cookies.get('organization_name')
	const navigate = useNavigate()
	const { register, setValue, watch } = useForm()
	const [filterSelectedOptions, setFilterSelectedOptions] =
		useState<boolean>(false)
	const allConnectionsRef = useRef<HTMLInputElement>(null)

	const [events, setEvents] = useState<Array<CalendarEvent>>([])
	const { currentMonth, currentWeek, view } = useAgenda()

	const [contentType, setContentType] = useState<DropdownFullItemProps>(
		allContentTypesData[0]
	)
	const [allStatuses, setAllStatuses] = useState<DropdownFullItemProps>(
		allStatusesCalendar[0]
	)
	const { inputSearch, handleChangeDebounce } = useSearchDebounce(
		watch('search')
	)

	const { isLoading, isRefetching } = useQuery({
		queryKey: [
			GET_CALENDAR_DATA,
			organizationId,
			view,
			currentMonth,
			currentWeek,
			contentType.value,
			allStatuses.value,
			inputSearch,
			filterSelectedOptions
		],
		queryFn: () =>
			getCalendarData(organizationId, {
				date_filter_type: view,
				selected_date: format(
					view === 'week' ? currentWeek : currentMonth,
					'yyyy-MM-dd'
				),
				search: inputSearch ?? undefined,
				content_category: filterSelectedOptions
					? undefined
					: contentType.value ?? undefined,
				status: filterSelectedOptions
					? undefined
					: allStatuses.value ?? undefined,
				only_notifications: filterSelectedOptions ? true : undefined
			}),
		onSuccess: (data: Array<GetCalendarDataPromise>) => {
			const dataCalendar = data
				.sort((a, b) => (a.publish_date < b.publish_date ? -1 : 1))
				.map(
					item =>
						({
							id: item.id,
							title: item.title,
							status: item.card_expirated ? 'expired' : item.status,
							organization_icon: organizationLogo,
							organization_name: organizationName,
							description: item.description,
							publish_date: item.publish_date,
							expire_date: item.expiration_date
								? item.expiration_date
								: item.expirate_date,
							isContent: item.type === 'content',
							isPush: item.type === 'notification',
							assets: item.assets ?? [],
							has_notification: item.has_notification ?? false,
							card_expirated: item.card_expirated ?? false,
							release_date: item.release_date ?? null,
							repeat_start_date: item.repeat_start_date ?? null
						} as CalendarEvent)
				)
			setEvents(dataCalendar)
		},
		onError: error => {
			if (error instanceof Error) {
				showToast('Error', error.message, 'error')
			}
		},
		refetchOnWindowFocus: false
	})

	const renderDropdowns = (): JSX.Element => (
		<div className="flex items-center gap-2">
			<DropdownFull
				rounded
				color={filterSelectedOptions ? 'gray3' : 'green'}
				item={contentType}
				setItem={setContentType}
				list={allContentTypesData}
				icon={Icons.arrowDownFull}
				className="min-w-[150px] md:min-w-[180px] flex-shrink-0"
				iconClass={`w-[10px] ${
					filterSelectedOptions ? 'text-lavender' : 'text-blue-primary'
				}`}
				fillPath
				disabled={filterSelectedOptions}
			/>
			<DropdownFull
				rounded
				color={filterSelectedOptions ? 'gray3' : 'green'}
				item={allStatuses}
				setItem={setAllStatuses}
				list={allStatusesCalendar}
				icon={Icons.arrowDownFull}
				className="min-w-[120px] md:min-w-[138px] flex-shrink-0"
				iconClass={`w-[10px] ${
					filterSelectedOptions ? 'text-lavender' : 'text-blue-primary'
				}`}
				fillPath
				disabled={filterSelectedOptions}
			/>
		</div>
	)

	return (
		<Layout>
			<div className=" 3xl:mx-auto 3xl:my-auto 3xl:w-3/4 mx-auto">
				<Title icon={Icons.speakerNotes} title="Content Hub" />
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between my-2 gap-3">
					<div className="w-auto sm:w-auto">
						<Button
							type="button"
							label="Go Back"
							color="white"
							className="!min-w-[91px] border-gray-3 border-[1px]"
							onClick={() => navigate(-1)}
							iconLeft={Icons.arrowBack}
						/>
					</div>
					<div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto md:w-2/3">
						<div className="flex items-center gap-4 min-w-full md:min-w-max overflow-x-auto">
							<Button
								label="Add New Content Item"
								iconLeft={Icons.add}
								onClick={() => navigate('/content/create')}
								className="min-w-[224px]"
							/>
							<Button
								label="New Direct Push Notification"
								iconLeft={Icons.add}
								onClick={() => navigate('/content/push-notification')}
								className="min-w-[269px]"
							/>
						</div>
						<Input
							inputFocus
							name="search"
							borderFull={false}
							register={register}
							className="w-full md:w-[50%] lg:w-[60%] flex-grow"
							inputClassName="p-[unset] px-2 !rounded-[16px]"
							placeholder="Search"
							onChangeCustom={handleChangeDebounce}
							rightIcon={Icons.cancel}
							rightClick={() => setValue('search', '')}
						/>
					</div>
				</div>
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 w-full sm:w-auto my-3">
					<div className="flex items-center gap-1 whitespace-nowrap">
						<Checkbox
							onChange={e => setFilterSelectedOptions(e.target.checked)}
							ref={allConnectionsRef}
							className="w-3 h-3 border-gray-9"
							checked={filterSelectedOptions}
						/>
						<Typography
							className="!font-normal capitalize font-weight-500 leading-12/15"
							type={TypographyTypes.span}
							fontLeading="12/16"
							title="Only Direct Pushes"
						/>
					</div>
					{filterSelectedOptions ? (
						<Tooltip
							icon={Icons.info}
							iconClassName="w-[20px] h-[20px] flex-shrink-0 text-blue-primary"
							text="Deselect the notification filter to use these options."
						>
							{renderDropdowns()}
						</Tooltip>
					) : (
						renderDropdowns()
					)}
				</div>
				<div className="py-2 px-1 bg-white rounded-lg w-full !min-h-[140px] h-[calc(100vh-250px)]">
					{isLoading || isRefetching ? (
						<div className="flex h-full w-full justify-center items-center">
							<CircularProgress disableShrink />
						</div>
					) : (
						<>
							<Calendar events={events} />
							<div className="flex justify-end w-full mt-4">
								<div className="py-2 px-1 bg-white rounded-lg">
									<div className="flex flex-wrap gap-6">
										{Object.entries(eventStyles)
											.reverse()
											.map(([status, style]) => (
												<div key={status} className="flex items-center gap-2">
													<div
														className={clsx(
															'w-4 h-4 rounded-full',
															style.statusColor.split(' ')[0],
															style.border
														)}
													/>

													<span className="text-sm text-gray-700 capitalize">
														{status}
													</span>
												</div>
											))}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</Layout>
	)
}
